import React from 'react';
import { CheckCircle2 } from 'lucide-react';
import { useInView } from '@/hooks/useInView';

const INDUSTRY_IMAGES = {
  'oil-gas': 'https://images.unsplash.com/photo-1588011930968-eadac80e6a5a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwxfHxvaWwlMjByZWZpbmVyeXxlbnwwfHx8fDE3NzA4NjM4MjB8MA&ixlib=rb-4.1.0&q=85',
  'mining': 'https://images.unsplash.com/photo-1523848309072-c199db53f137?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxOTB8MHwxfHNlYXJjaHwxfHxtaW5pbmclMjBlcXVpcG1lbnR8ZW58MHx8fHwxNzcwODYzODI4fDA&ixlib=rb-4.1.0&q=85',
  'refineries': 'https://images.unsplash.com/photo-1678532685208-54acdd41187d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHwzfHxvaWwlMjByZWZpbmVyeXxlbnwwfHx8fDE3NzA4NjM4MjB8MA&ixlib=rb-4.1.0&q=85',
  'power': 'https://images.unsplash.com/photo-1578776349090-de61da00ff1a?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxwb3dlciUyMHBsYW50fGVufDB8fHx8MTc3MDg2MzgzOHww&ixlib=rb-4.1.0&q=85',
  'construction': 'https://images.unsplash.com/photo-1599995903128-531fc7fb694b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODl8MHwxfHNlYXJjaHwxfHxjb25zdHJ1Y3Rpb24lMjBzaXRlfGVufDB8fHx8MTc3MDg2Mzg1N3ww&ixlib=rb-4.1.0&q=85',
  'engineering': 'https://images.unsplash.com/photo-1759159091728-e2c87b9d9315?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwyfHxwcmVjaXNpb24lMjBtYWNoaW5lcnl8ZW58MHx8fHwxNzcwODYzODY3fDA&ixlib=rb-4.1.0&q=85',
  'student-accommodation': 'https://images.unsplash.com/photo-1618831352005-83a8a8b65c6d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzF8MHwxfHNlYXJjaHwxfHxzdHVkZW50JTIwYWNjb21tb2RhdGlvbnxlbnwwfHx8fDE3NzEzOTU3NTd8MA&ixlib=rb-4.1.0&q=85',
  'facilities': 'https://images.unsplash.com/photo-1606836379799-f88b03bc7039?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxmYWNpbGl0aWVzJTIwbWFuYWdlbWVudHxlbnwwfHx8fDE3NzEzOTU3Njh8MA&ixlib=rb-4.1.0&q=85',
  'general': 'https://images.unsplash.com/photo-1758630737361-ca7532fb5e7f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxvZmZpY2UlMjBvcGVyYXRpb25zfGVufDB8fHx8MTc3MTM5NTc3Nnww&ixlib=rb-4.1.0&q=85',
};

const INDUSTRY_KEYS = [
  { id: 'oil-gas', translationKey: 'oilGas' },
  { id: 'mining', translationKey: 'mining' },
  { id: 'refineries', translationKey: 'refineries' },
  { id: 'power', translationKey: 'power' },
  { id: 'construction', translationKey: 'construction' },
  { id: 'engineering', translationKey: 'engineering' },
  { id: 'student-accommodation', translationKey: 'studentAccommodation' },
  { id: 'facilities', translationKey: 'facilities' },
  { id: 'general', translationKey: 'general' },
];

export const IndustriesSection = ({ t }) => {
  const [ref, isInView] = useInView();

  const industries = INDUSTRY_KEYS.map(({ id, translationKey }) => ({
    id,
    title: t.industries[translationKey].title,
    description: t.industries[translationKey].description,
    image: INDUSTRY_IMAGES[id],
    items: t.industries[translationKey].items,
  }));

  return (
    <section id="industries" className="py-20 bg-white" data-testid="industries-section">
      <div ref={ref} className={`max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 transition-all duration-700 ${isInView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4" data-testid="industries-title">{t.industries.title}</h2>
          <div className="w-20 h-1 bg-blue-600 mx-auto mb-4"></div>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto" data-testid="industries-subtitle">
            {t.industries.subtitle}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {industries.map((industry) => (
            <div
              key={industry.id}
              className="group bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2"
              data-testid={`industry-card-${industry.id}`}
            >
              <div className="relative h-64 overflow-hidden">
                <img
                  src={industry.image}
                  alt={industry.title}
                  loading="lazy"
                  className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                  data-testid={`industry-image-${industry.id}`}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent"></div>
                <h3 className="absolute bottom-4 left-4 text-2xl font-bold text-white" data-testid={`industry-title-${industry.id}`}>
                  {industry.title}
                </h3>
              </div>
              <div className="p-6">
                <p className="text-gray-700 mb-4 leading-relaxed" data-testid={`industry-description-${industry.id}`}>
                  {industry.description}
                </p>
                <div className="space-y-2">
                  {industry.items.map((item, idx) => (
                    <div key={idx} className="flex items-center text-sm text-gray-600" data-testid={`industry-item-${industry.id}-${idx}`}>
                      <CheckCircle2 size={16} className="text-blue-600 mr-2 flex-shrink-0" />
                      {item}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};
